import React from 'react';
import { renderProcessedText } from '../../utils/textRenderer';

interface OffersCardProps {
  // Direct content format (used in carousel)
  content?: Array<{
    header: string;
    title: string;
    description: string;
    profileImage?: string;
    options: Array<{
      key: string;
      value: string;
    }>;
    discountAmount: string;
    actualAmount: string;
    currency: string;
    cta: string;
    period?: string;
  }>;
  // Transformed props format (used in individual rendering)
  header?: string;
  title?: string;
  description?: string;
  price?: string;
  originalPrice?: string;
  discount?: string;
  ctaText?: string;
  features?: Array<string>;
  onCtaClick?: (offer: unknown) => void;
  onSendMessage?: (content: string) => void;
  isCarousel?: boolean;
}

const OffersCard: React.FC<OffersCardProps> = ({ 
  content, 
  header,
  title, 
  description, 
  price, 
  originalPrice, 
  discount, 
  ctaText, 
  features, 
  onCtaClick,
  onSendMessage,
  isCarousel
}) => {
  // Debug logging to understand what props are being passed
  console.log('OffersCard received props:', {
    content,
    header,
    title,
    description,
    price,
    originalPrice,
    discount,
    ctaText,
    features
  });

  // Determine which data structure we're working with
  const isOriginalDataStructure = content && Array.isArray(content) && content.length > 0;
  
  console.log('Data structure detection:', {
    isOriginalDataStructure,
    contentExists: !!content,
    isArray: Array.isArray(content),
    contentLength: content?.length
  });
  
  // Handle original data structure (carousel mode)
  if (isOriginalDataStructure) {
    console.log('Using original data structure, content:', content);
    return (
      <div className="offers-card" style={{ height: isCarousel ? '100%' : 'auto' }}>
        {content.map((offer, index) => (
          <div key={index} className="offers-card__item">
            <div className="offers-card__header">{offer?.header || 'OFFER'}</div>
            <h4 className="offers-card__title">{renderProcessedText(offer?.title || 'Special Offer')}</h4>
            
            {offer?.description && (
              <p className="offers-card__description">{renderProcessedText(offer.description)}</p>
            )}
            
            {/* Key-Value pairs section */}
            <div className="offers-card__options">
              {offer.options && Array.isArray(offer.options) && offer.options.map((option, optionIndex) => (
                <div key={optionIndex} className="offers-card__option">
                  <span className="offers-card__option-key">{option.key}:</span>
                  <span className="offers-card__option-value">{option.value}</span>
                </div>
              ))}
            </div>
            
            {/* Pricing section */}
            <div className="offers-card__pricing">
              <div className="offers-card__price-main">
                {(
                  <span className="offers-card__discount-price">
                    {offer?.discountAmount}{offer?.period ? `/${offer.period}` : '/m'}
                  </span>
                )}
                {(
                  <span className="offers-card__original-price">
                    {offer?.actualAmount}{offer?.period ? `/${offer.period}` : '/m'}
                  </span>
                )}
              </div>
            </div>
            
            <div className="offers-card__cta-wrapper">
              <button 
                className={`offers-card__cta ${(offer?.cta || 'Select').length > 12 ? 'long-text' : ''}`}
                onClick={() => {
                  const messageText = `${offer?.cta || 'Select'} ${offer?.title || 'Special Offer'}`;
                  onSendMessage?.(messageText);
                  onCtaClick?.(offer);
                }}
              >
                {offer?.cta || 'Select'}
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Handle transformed props structure (individual rendering mode)
  if (!title && !description && !features) {
    return <div className="offers-card-error">No offers available</div>;
  }

  console.log('Using transformed props structure');
  return (
    <div className="offers-card" style={{ height: isCarousel ? '100%' : 'auto' }}>
      <div className="offers-card__item">
        <div className="offers-card__header">{header || 'OFFER'}</div>
        {title && (
          <h4 className="offers-card__title">{renderProcessedText(title)}</h4>
        )}
        
        {description && (
          <p className="offers-card__description">{renderProcessedText(description)}</p>
        )}
        
        {/* Features section (converted from key-value pairs) */}
        <div className="offers-card__options">
          {features && Array.isArray(features) && features.map((feature, featureIndex) => {
            const [key, value] = feature.split(': ');
            return (
              <div key={featureIndex} className="offers-card__option">
                <span className="offers-card__option-key">{key}:</span>
                <span className="offers-card__option-value">{value}</span>
              </div>
            );
          })}
        </div>
        
        {/* Pricing section */}
        {(
          <div className="offers-card__pricing">
            <div className="offers-card__price-main">
              {(
                <span className="offers-card__discount-price">
                  {price}
                </span>
              )}
              {(
                <span className="offers-card__original-price">
                  {originalPrice}
                </span>
              )}
            </div>
           
          </div>
        )}
        
        <div className="offers-card__cta-wrapper">
          <button 
            className={`offers-card__cta ${(ctaText || 'Select').length > 12 ? 'long-text' : ''}`}
            onClick={() => {
              const messageText = `${ctaText || 'Select'} ${title || 'Special Offer'}`;
              onSendMessage?.(messageText);
              onCtaClick?.({});
            }}
          >
            {ctaText || 'Select'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OffersCard;
